import React from 'react';
import { api } from '../lib/api';
import { useSnackbar } from '../contexts/SnackbarContext';
import { Btn, Modal } from './index';
import { palette as T, type } from '../design';


function Star({ filled, onClick, onMouseEnter, onMouseLeave }) {
  return (
    <button
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        background: 'none', border: 'none', cursor: 'pointer', fontSize: type.h2,
        color: filled ? T.terracotta : T.border, padding: '0 4px', lineHeight: 1,
        transition: 'color 0.15s',
      }}
    >
      ★
    </button>
  );
}

export function ReviewModal({ booking, onClose, onSubmitted }) {
  const { showSnackbar } = useSnackbar();
  const [rating, setRating] = React.useState(0);
  const [hoverRating, setHoverRating] = React.useState(0);
  const [text, setText] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [submitted, setSubmitted] = React.useState(false);
  const titleId = React.useId();

  const handleSubmit = async () => {
    if (!rating) {
      setError('Please select a rating.');
      return;
    }
    if (!text.trim()) {
      setError('Please write a review.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await api.post('/reviews', { bookingId: booking.id, rating, text: text.trim() });
      setSubmitted(true);
      showSnackbar('Review submitted. Thank you!', { variant: 'success' });
      setTimeout(() => onSubmitted?.(), 1200);
    } catch (err) {
      const msg = err.data?.message || err.message || 'Failed to submit review';
      setError(msg);
      showSnackbar(msg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // The shared dialog (DS-2): named by whichever heading shows, closed by Escape or Close, and
  // held open while the review is on its way.
  return (
    <Modal onClose={loading ? undefined : onClose} maxWidth={480} labelledBy={titleId}>
      <div style={{ padding: '32px 28px' }}>
        {submitted ? (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div aria-hidden="true" style={{ fontSize: type.h1, marginBottom: 12 }}>✓</div>
            <h3 id={titleId} style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 22, color: T.espresso, margin: '0 0 8px' }}>Review submitted!</h3>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, margin: '0 0 24px' }}>Thank you for leaving a review.</p>
            <Btn onClick={onClose}>Close</Btn>
          </div>
        ) : (
          <>
            <h3 id={titleId} style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h3, color: T.espresso, margin: '0 40px 4px 0' }}>How was your session?</h3>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood, margin: '0 0 20px' }}>Your review helps other students find great instructors.</p>

            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
              {[1, 2, 3, 4, 5].map(n => (
                <Star
                  key={n}
                  filled={n <= (hoverRating || rating)}
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHoverRating(n)}
                  onMouseLeave={() => setHoverRating(0)}
                />
              ))}
            </div>

            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="Share your experience..."
              rows={4}
              style={{
                width: '100%', padding: '12px 14px', borderRadius: 12, border: '1px solid ' + T.border,
                fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.espresso,
                resize: 'vertical', outline: 'none', marginBottom: 16,
                boxSizing: 'border-box',
              }}
            />

            {error && <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.errorRed, margin: '0 0 12px' }}>{error}</p>}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Btn variant="ghost" onClick={onClose} disabled={loading}>Cancel</Btn>
              <Btn onClick={handleSubmit} disabled={loading}>{loading ? 'Submitting…' : 'Submit review'}</Btn>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
